import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { requestGetAllDatasets } from "../../store/slices/datasets";

import { MapSettingsWrapper } from "./styles";
import { getCurrentProjectData } from "../../store/selectors/projects";
import { IFormatting, IMarkersType, WidgetItem } from "../../models/Widgets";
import {
  getQualitativePalette,
  SequentialColors,
} from "../../constants/colors";
import WidgetsConfig from "./components/WidgetsConfig/WidgetsConfig";
import MapConfig from "./components/MapConfig/MapConfig";

interface MapSettingsProps {
  currentWidget: WidgetItem;
  widgetData: WidgetItem;
  setWidgetData: React.Dispatch<React.SetStateAction<WidgetItem>>;
  setSelectedChart: (chart?: string) => void;
  fullScreenLayout: boolean;
}

export const MapSettings = ({
  currentWidget,
  widgetData,
  setWidgetData,
  setSelectedChart,
  fullScreenLayout,
}: MapSettingsProps) => {
  const dispatch = useDispatch();

  const projectData = useSelector(getCurrentProjectData);

  const [selectedKey, setSelectedKey] = useState<string | undefined>(() => {
    if (widgetData?.groupBy) {
      return undefined;
    }
    return "default";
  });

  const [paletteIndex, setPaletteIndex] = useState<number>(0);
  const [colorRanges, setColorRanges] = useState<number[]>([]);

  const [selectedFormatting, setSelectedFormatting] = useState<
    IFormatting | undefined
  >(
    widgetData?.formatting && widgetData?.formatting?.length === 1
      ? widgetData?.formatting[0]
      : undefined
  );

  const [selectedMarker, setSelectedMarker] = useState<
    IMarkersType | undefined
  >(
    widgetData?.markers && widgetData?.markers?.length === 1
      ? widgetData?.markers[0]
      : undefined
  );

  const selectedDimensions = [
    ...(widgetData.arrangeBy ?? []),
    ...(widgetData.groupBy ?? []),
    ...(widgetData.display ?? []),
    ...(widgetData.xAxe ?? []),
    ...(widgetData.yAxe ?? []),
  ];

  const selectedColors =
    widgetData?.formatting?.map((item) => item.color) ?? [];

  const currentChartPalette = () => {
    if (
      ["mapChart", "matrix", "punchcardChart", "sankey"].includes(
        widgetData.chartType
      )
    ) {
      return SequentialColors;
    }

    return getQualitativePalette(widgetData?.formatting?.length);
  };

  useEffect(() => {
    dispatch(requestGetAllDatasets({}));
  }, [dispatch]);

  useEffect(() => {
    const palette = currentChartPalette();

    if (Array.isArray(palette)) {
      // eslint-disable-next-line array-callback-return
      palette.find((item, index) => {
        if (item.id === widgetData.palette?.paletteId) {
          setPaletteIndex(index);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setWidgetData({
      ...widgetData,
      palette: {
        ...widgetData.palette,
        range: colorRanges,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colorRanges]);

  return (
    <MapSettingsWrapper>
      {["mapChart", "map_matrix"].includes(widgetData.chartType) ? (
        <MapConfig
          currentWidget={currentWidget}
          widgetData={widgetData}
          setWidgetData={setWidgetData}
          projectData={projectData}
          selectedDimensions={selectedDimensions}
          fullScreenLayout={fullScreenLayout}
          setSelectedChart={setSelectedChart}
        />
      ) : (
        <WidgetsConfig
          currentWidget={currentWidget}
          widgetData={widgetData}
          setWidgetData={setWidgetData}
          projectData={projectData}
          selectedDimensions={selectedDimensions}
          setSelectedMarker={setSelectedMarker}
          setSelectedKey={setSelectedKey}
          selectedColors={selectedColors}
          setSelectedFormatting={setSelectedFormatting}
          colorRanges={colorRanges}
          setColorRanges={setColorRanges}
          paletteIndex={paletteIndex}
          setPaletteIndex={setPaletteIndex}
          currentChartPalette={currentChartPalette}
          selectedMarker={selectedMarker}
          selectedKey={selectedKey}
          selectedFormatting={selectedFormatting}
          setSelectedChart={setSelectedChart}
        />
      )}
    </MapSettingsWrapper>
  );
};
